/* eslint-disable max-lines-per-function */
import { find } from 'lodash';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getCurrentUser } from 'src/models/lmsConfig/queries';
import { getPerformanceCards } from 'src/models/lmsConfig/selectors';
import { setPerformanceCards } from 'src/models/lmsConfig/slice';
import { getState } from 'src/store';
import axios from 'src/workspace/axios';
import { getModuleProps, isLmsWeb } from 'src/workspace/utils';
import { SUPPORTED_PERFORMANCE_CARDS } from './constant';
import {
  clearPerformanceState,
  setKeyMetrics,
  setKeyMetricsMetadata,
  setLeaderboards,
  setRankings,
  setStatus,
} from './slice';
import { FetchUserMetricParams } from './types';

export const fetchUserMetric = async ({
  goalDefinitionCode,
  userId,
  frequency,
  filters,
  meta,
  drilldownGroupBy,
  extraParams = {},
}: FetchUserMetricParams) => {
  const computedFilters =
    typeof filters === 'string'
      ? filters
      : JSON.stringify({
          user_id: userId,
          ...(frequency ? { frequency } : {}),
          ...(filters || {}),
        });

  const { data } = await axios.get('/performance/v1/users/metrics', {
    params: {
      goal_definition_code: goalDefinitionCode,
      filters: computedFilters,
      drilldown_meta: JSON.stringify(meta || { user_id: userId }),
      ...(drilldownGroupBy
        ? { drilldown_group_by: JSON.stringify(drilldownGroupBy) }
        : {}),
      ...extraParams,
    },
  });

  return data;
};

const fetchLeaderboards = createAsyncThunk(
  'leaderboards',
  async (_, { dispatch }) => {
    try {
      const { data } = await axios.post('/performance/v1/leaderboard/list', {
        timestamp: new Date().getTime(),
        user_id: getCurrentUser()?.code,
        filters: '{}',
        source: 'web',
      });
      dispatch(setLeaderboards(data?.result?.leaderboard_configs || []));
    } catch (e) {
      dispatch(setStatus({ leaderboards_list: 'error' }));
    }
  },
);

export const fetchKeyMetricsMetadata = createAsyncThunk(
  'keyMetricsMetadata',
  async ({ cards, filters = {} }: any, { dispatch }) => {
    try {
      dispatch(setKeyMetricsMetadata([]));
      dispatch(setStatus({ key_metrics_metadata: 'loading' }));
      const userId = getCurrentUser()?.code;
      const metricApiCalls = cards?.map((item) =>
        fetchUserMetric({
          goalDefinitionCode: item?.goal_definition_code,
          userId,
          frequency: item?.frequency,
          extraParams: {
            from_web2: true,
          },
          filters,
        })
          .then((metricData) => ({
            title: item?.title,
            code: item.goal_definition_code,
            card: item,
            data: metricData,
            success: true,
          }))
          .catch((error) => {
            // eslint-disable-next-line
            console.error(
              'Failed to fetch metric for',
              item.goal_definition_code,
              error,
            );
            return {
              title: item?.title,
              code: item.goal_definition_code,
              data: null,
              success: false,
              error: error.message,
            };
          }),
      );

      const metricResults = await Promise.allSettled(metricApiCalls);

      const successfulMetrics = metricResults
        .filter(
          (result) => result.status === 'fulfilled' && result.value.success,
        )
        .map((result) => {
          if (result.status === 'fulfilled') {
            return {
              title: result.value?.title,
              code: result.value.code,
              data: result.value.data,
              card: result?.value?.card,
            };
          }
          return null;
        })
        .filter(Boolean);

      dispatch(setKeyMetricsMetadata(successfulMetrics));
      dispatch(setStatus({ key_metrics_metadata: 'done' }));
    } catch (e) {
      dispatch(setStatus({ key_metrics_metadata: 'error' }));
    }
  },
);

export const fetchKeyMetrics = createAsyncThunk(
  'keyMetrics',
  async ({ limit, filters = {}, skip = 0 }: any, { dispatch }) => {
    try {
      dispatch(setStatus({ key_metrics: 'loading' }));
      const { data } = await axios.get('/performance/v1/users/cards', {
        params: {
          filters: JSON.stringify(filters),
          limit,
          skip,
        },
      });

      const {
        cards,
        goal_groups: goalGroups,
        filters: filterConfig,
      } = data?.body || {};

      dispatch(
        setKeyMetrics({
          goal_groups: goalGroups,
          cards,
          filters: filterConfig,
        }),
      );
      dispatch(setStatus({ key_metrics: 'done' }));
    } catch (e) {
      dispatch(setStatus({ key_metrics: 'error' }));
    }
  },
);

export const fetchDashboardRankings = createAsyncThunk(
  'dashboardRankings',
  async ({ payload }: any, { dispatch }) => {
    try {
      const response = await axios.post(
        '/performance/v1/leaderboard/rankings',
        payload,
      );

      const { data } = response?.data?.result || {};
      const { ranking_list_view_type: rankingListViewType } =
        response?.data?.result || {};
      const rankings = data?.table?.data || [];
      const rankingsMeta = {
        lastUpdated: data?.meta?.last_updated,
        startDate: data?.meta?.leaderboard_start_date,
      };
      const individual = data?.individual || {};

      const columns = Object.keys(data?.table?.headings || {}).map((key) => ({
        field: key,
        title: data?.table?.headings[key],
        name: data?.table?.headings[key],
      }));

      dispatch(
        setRankings({
          data: rankings,
          table: {
            columns,
            title: data?.table?.title || '',
          },
          meta: rankingsMeta,
          individual,
          listViewOptions: rankingListViewType?.code_name_spinner_options || [
            { code: 'all', name: 'All' },
          ],
        }),
      );
    } catch (e) {
      dispatch(setStatus({ rankings: 'error' }));
    }
  },
);

const onUpdate = async ({ dispatch, props }) => {
  dispatch(clearPerformanceState());
  let performanceCards = [];
  if (isLmsWeb()) {
    performanceCards = getModuleProps()?.getPerformanceCards?.() || [];
    dispatch(
      setPerformanceCards(
        performanceCards.filter((card: any) =>
          SUPPORTED_PERFORMANCE_CARDS.includes(card.code),
        ),
      ),
    );
  } else {
    performanceCards = getPerformanceCards(getState());
  }

  switch (props.tab) {
    case 'home':
      if (find(performanceCards, { code: 'key_metrics' })) {
        dispatch(fetchKeyMetrics({ limit: 6 }));
      }
      if (find(performanceCards, { code: 'leaderboard' })) {
        dispatch(fetchLeaderboards());
      }
      break;
    case 'leaderboard':
      dispatch(fetchLeaderboards());
      break;

    default:
      break;
  }
};

const onActivate = () => {};

const onDeactivate = () => {};

export default { onActivate, onDeactivate, onUpdate };
